import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { PERMISSION_ENTITY_INVOICE } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import type { InvoiceCreateWire, InvoiceUpdateWire } from '@klear/invoice/invoice.wire'
import type { Invoice } from '@klear/invoice/invoice'

export type InvoiceTimelineForm = {
  effectiveDate: KlearDate
  endDate: KlearDate
  number?: string
  notes?: string
  amount: Money
}

export type InvoiceEventFormValues = InvoiceTimelineForm & {
  type: typeof PERMISSION_ENTITY_INVOICE
  documents?: OrderEventDocumentRef[]
  files?: File[]
}

export type InvoiceTimelineFormErrorMap = Record<string, string | undefined>

export type InvoiceParentDefaults = {
  amount?: Money
  endDate?: KlearDate
}

export function invoiceEventFormFromInvoice(invoice: Invoice): InvoiceEventFormValues {
  return {
    type: PERMISSION_ENTITY_INVOICE,
    effectiveDate: invoice.effectiveDate,
    endDate: invoice.endDate,
    number: invoice.number,
    notes: invoice.notes,
    documents: [],
    amount: invoice.amount
  }
}

export function createInvoiceEventForm(parent?: InvoiceParentDefaults): InvoiceEventFormValues {
  return {
    type: PERMISSION_ENTITY_INVOICE,
    effectiveDate: KlearDate.today(),
    notes: '',
    amount: parent?.amount ?? new Money(),
    endDate: parent?.endDate ?? KlearDate.today(),
    documents: []
  }
}

export function validateInvoiceEventForm(form: InvoiceTimelineForm): InvoiceTimelineFormErrorMap {
  const errors: InvoiceTimelineFormErrorMap = {}
  const amount = form.amount
  if (amount == null || !amount.greaterThan(amount.zero)) {
    errors.amount = 'Amount is required'
  }
  if (form.endDate && form.effectiveDate && form.endDate.isBefore(form.effectiveDate, 'day')) {
    errors.endDate = 'Collection date cannot be before the invoice date'
  }
  return errors
}

export function invoiceEventFormToCreateWire(
  form: InvoiceTimelineForm,
  orderId: number,
  parentEventId: number | undefined
): InvoiceCreateWire {
  const wire: InvoiceCreateWire = {
    orderId,
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    endDate: form.endDate.toJSON()
  }
  if (form.number) wire.number = form.number
  if (form.notes) wire.notes = form.notes
  if (parentEventId != null && parentEventId > 0) wire.parentEventId = parentEventId
  return wire
}

export function invoiceEventFormToUpdateWire(
  form: InvoiceTimelineForm,
  parentEventId: number | undefined
): InvoiceUpdateWire {
  const wire: InvoiceUpdateWire = {
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    endDate: form.endDate.toJSON(),
    number: form.number?.trim() || null,
    notes: form.notes?.trim() || null
  }
  if (parentEventId != null && parentEventId > 0) wire.parentEventId = parentEventId
  return wire
}
